/**
 * Maps the Add-Product form state to the `VendorCatalogAddInput` GraphQL input.
 *
 * Field names/types below are confirmed against the live schema
 * (`VendorCatalogAddInput`). Required (NON_NULL) fields: `subcategory`,
 * `availableQuantity`, `minimumOrderQuantity`, `isGstIncluded`. `vendorId` is
 * injected by `useAddProduct` from the auth context, not collected in the form.
 */

const emptyToUndefined = (value) => {
  if (value == null) return undefined;
  const trimmed = String(value).trim();
  return trimmed === '' ? undefined : trimmed;
};

const toNumber = (value) => {
  if (value === '' || value == null) return undefined;
  const num = Number(value);
  return Number.isNaN(num) ? undefined : num;
};

const omitUndefined = (object) =>
  Object.fromEntries(Object.entries(object).filter(([, value]) => value !== undefined));

export function buildCatalogInput(form) {
  return omitUndefined({
    // Descriptive
    name: emptyToUndefined(form.name),
    description: emptyToUndefined(form.description),
    shortDescription: emptyToUndefined(form.shortDescription),
    brand: emptyToUndefined(form.brand),

    // Classification
    category: emptyToUndefined(form.category),
    subcategory: emptyToUndefined(form.subcategory), // String! (required)
    hsnCode: emptyToUndefined(form.hsnCode),

    // Packaging
    unit: emptyToUndefined(form.unit),
    unitSize: emptyToUndefined(form.unitSize), // String in schema
    weight: toNumber(form.weight),

    // Images — first is the primary, the rest go to imageUrls.
    primaryImageUrl: form.images?.[0] || undefined,
    imageUrls: form.images && form.images.length > 1 ? form.images.slice(1) : undefined,

    // Pricing
    vendorPrice: toNumber(form.vendorPrice),
    mrp: toNumber(form.mrp),
    isGstIncluded: Boolean(form.isGstIncluded), // Boolean! (required)

    // Inventory
    availableQuantity: toNumber(form.availableQuantity), // Float! (required)
    minimumOrderQuantity: toNumber(form.minimumOrderQuantity), // Float! (required)
    lowStockThreshold: toNumber(form.lowStockThreshold),
    estimatedPrepTime: toNumber(form.estimatedPrepTime), // Int

    // Dietary attributes
    isVegetarian: form.isVegetarian || undefined,
    isVegan: form.isVegan || undefined,
    isGlutenFree: form.isGlutenFree || undefined,
    isDairyFree: form.isDairyFree || undefined,
    isNutFree: form.isNutFree || undefined,
    isHalal: form.isHalal || undefined,
    isKeto: form.isKeto || undefined,
    isOrganic: form.isOrganic || undefined,
  });
}

/** Required fields for client-side validation before submitting. */
export const REQUIRED_FIELDS = {
  name: 'Product name is required.',
  category: 'Select a category.',
  subcategory: 'Select a subcategory.',
  vendorPrice: 'Enter your selling price.',
  availableQuantity: 'Enter the available stock quantity.',
  minimumOrderQuantity: 'Enter the minimum order quantity.',
};

/** Numeric fields that, when provided, must parse to a finite, non-negative number. */
const NON_NEGATIVE_NUMERIC = {
  vendorPrice: 'Enter a valid price.',
  mrp: 'Enter a valid MRP.',
  weight: 'Enter a valid weight.',
  availableQuantity: 'Enter a valid quantity.',
  minimumOrderQuantity: 'Enter a valid quantity.',
  lowStockThreshold: 'Enter a valid number.',
  estimatedPrepTime: 'Enter a valid number.',
};

export function validateCatalogForm(form) {
  const errors = {};

  // Required fields must be present.
  Object.entries(REQUIRED_FIELDS).forEach(([field, message]) => {
    const value = form[field];
    if (value == null || String(value).trim() === '') errors[field] = message;
  });

  // Numeric fields, when provided, must be finite and >= 0. This also stops a
  // non-numeric required value (e.g. "12.3.4") from passing the presence check
  // and then being silently dropped by toNumber() / omitUndefined().
  Object.entries(NON_NEGATIVE_NUMERIC).forEach(([field, message]) => {
    if (errors[field]) return; // already flagged as required-but-empty
    const raw = form[field];
    if (raw === '' || raw == null) return; // optional & empty is fine
    const num = Number(raw);
    if (Number.isNaN(num) || num < 0) errors[field] = message;
  });

  return errors;
}

/**
 * Map a catalog item (READ model) onto ProductForm state for editing.
 *
 * Note the read/write asymmetry: the read model exposes `stockQuantity`, while
 * the write input expects `availableQuantity` — handled here.
 */
export function catalogItemToFormValues(item) {
  if (!item) return {};
  const str = (value) => (value == null ? '' : String(value));

  // First image is the primary; the rest are additional images.
  const primaryImage = item.vendorPrimaryImageUrl || item.vendorPrimaryImageUrlThumbnail || '';
  const images = [primaryImage, ...(item.vendorImageUrls || []).filter((url) => url && url !== primaryImage)].filter(
    Boolean,
  );

  return {
    name: item.vendorProductName || item.name || '',
    brand: item.brand || '',
    shortDescription: item.shortDescription || '',
    description: item.vendorDescription || item.description || '',
    category: item.category || '',
    subcategory: item.subcategory || '',
    hsnCode: item.hsnCode || '',
    unit: item.unit || '',
    unitSize: str(item.unitSize),
    weight: str(item.weight),
    images,
    vendorPrice: item.vendorPrice?.amount != null ? str(item.vendorPrice.amount) : '',
    mrp: str(item.mrp),
    isGstIncluded: Boolean(item.isGstIncluded),
    availableQuantity: str(item.stockQuantity),
    minimumOrderQuantity: str(item.minimumOrderQuantity),
    lowStockThreshold: str(item.lowStockThreshold),
    estimatedPrepTime: str(item.estimatedPrepTime),
    isVegetarian: !!item.isVegetarian,
    isVegan: !!item.isVegan,
    isGlutenFree: !!item.isGlutenFree,
    isDairyFree: !!item.isDairyFree,
    isNutFree: !!item.isNutFree,
    isHalal: !!item.isHalal,
    isKeto: !!item.isKeto,
    isOrganic: !!item.isOrganic,
  };
}
